/**
 * Shablloni Word — dokumenti i kompanisë me dizajnin e vet, plus lidhja e
 * fushave të tij me të dhënat e punëtorit.
 *
 * I kontrolluar nga faqja: `word = { file, tags, fieldMap }`. Teksti dhe
 * dizajni ndryshohen në Word dhe ngarkohen sërish — këtu vetëm shihen dhe
 * lidhen fushat.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, MenuItem, Select, Stack, Tooltip, Typography,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { CONTRACT_TEMPLATE_TAGS } from '../../config/contractTemplateTags';

const FIELD_OPTIONS = [
  { value: '@FirstName @LastName', label: 'Emri e mbiemri' },
  ...CONTRACT_TEMPLATE_TAGS.map((t) => ({ value: `@${t.key}`, label: t.key === 'CompanyLogo' ? 'Kompania – Logo (imazh nga profili)' : t.label })),
];
const labelOf = (v) => FIELD_OPTIONS.find((o) => o.value === v)?.label || v;

/** Pamja e dokumentit ashtu siç duket në Word (docx-preview). */
export function WordPreview({ file, maxHeight = '70vh' }) {
  const ref = useRef(null);
  const [state, setState] = useState('loading');
  useEffect(() => {
    let alive = true;
    if (!file || !ref.current) return undefined;
    setState('loading');
    (async () => {
      try {
        const { renderAsync } = await import('docx-preview');
        if (!alive || !ref.current) return;
        ref.current.innerHTML = '';
        await renderAsync(file, ref.current, undefined, {
          className: 'docx', inWrapper: true, ignoreWidth: false, ignoreHeight: false, breakPages: true,
        });
        if (alive) setState('ready');
      } catch {
        if (alive) setState('error');
      }
    })();
    return () => { alive = false; };
  }, [file]);
  return (
    <Box sx={{ position: 'relative', bgcolor: 'grey.200', borderRadius: 2, overflow: 'auto', maxHeight,
      '& .docx-wrapper': { bgcolor: 'transparent', p: 2 }, '& .docx-wrapper > section.docx': { boxShadow: '0 2px 10px rgba(0,0,0,.12)', mb: 2 } }}>
      {state === 'loading' && (
        <Stack alignItems="center" sx={{ py: 8 }}><CircularProgress size={24} /></Stack>
      )}
      {state === 'error' && <Alert severity="warning" sx={{ m: 2 }}>Pamja e dokumentit nuk u shfaq, por dokumenti ruhet dhe gjenerohet njësoj.</Alert>}
      <Box ref={ref} />
    </Box>
  );
}

export default function WordTemplatePanel({ word, busy, onFieldChange, onReplace, onRemove }) {
  const inputRef = useRef(null);
  const tags = word?.tags || [];
  const mapped = tags.filter((t) => word.fieldMap?.[t.key]).length;

  const download = () => {
    const url = URL.createObjectURL(word.file);
    const a = document.createElement('a');
    a.href = url; a.download = word.file.name || 'shabllon.docx'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: '#E8F0FE', color: '#1A56DB', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
            <DescriptionIcon fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700 }} noWrap>{word.file?.name || 'Dokumenti Word'}</Typography>
            <Typography variant="caption" color="text.secondary">
              Dizajni, tabelat dhe nënshkrimet mbeten si në Word · {mapped}/{tags.length} fusha të lidhura
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          <input ref={inputRef} type="file" accept=".docx" hidden
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onReplace(f); }} />
          <Button size="small" variant="outlined" startIcon={busy ? <CircularProgress size={14} /> : <SwapHorizIcon />}
            disabled={busy} onClick={() => inputRef.current?.click()} sx={{ textTransform: 'none' }}>
            Zëvendëso dokumentin
          </Button>
          <Tooltip title="Shkarko dokumentin Word"><span>
            <Button size="small" variant="outlined" onClick={download} disabled={!word.file} sx={{ minWidth: 0, px: 1 }}><DownloadIcon fontSize="small" /></Button>
          </span></Tooltip>
          <Tooltip title="Hiq Word-in — kthehu te teksti i redaktueshëm"><span>
            <Button size="small" color="error" variant="outlined" onClick={onRemove} sx={{ minWidth: 0, px: 1 }}><DeleteOutlineIcon fontSize="small" /></Button>
          </span></Tooltip>
        </Stack>
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0,1fr) 340px' }, gap: 2, alignItems: 'start' }}>
        <WordPreview file={word.file} />

        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
            <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Fushat e dokumentit</Typography>
            <Typography variant="caption" color="text.secondary">
              Secila zëvendësohet me të dhënat e punëtorit kur gjenerohet kontrata.
            </Typography>
          </Box>
          {tags.length === 0 && (
            <Alert severity="info" sx={{ m: 1.5 }}>
              Dokumenti s'ka fusha. Shkruani në Word p.sh. <b>{'{!first_name!}'}</b> aty ku duhet emri, ose <b>{'{!company_logo!}'}</b> për logon e kompanisë, dhe ngarkojeni sërish.
            </Alert>
          )}
          <Box sx={{ maxHeight: '62vh', overflow: 'auto' }}>
            {tags.map((t) => {
              const value = word.fieldMap?.[t.key] || '';
              return (
                <Box key={t.key} sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider' }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 0.75 }}>
                    <Box component="code" sx={{ fontSize: 12, bgcolor: 'grey.100', px: 0.75, py: 0.25, borderRadius: 1, overflowWrap: 'anywhere' }}>{t.raw}</Box>
                    {value
                      ? <Chip size="small" color="success" variant="outlined" label={t.suggestion === value ? 'auto' : 'lidhur'} sx={{ height: 20, fontSize: 11 }} />
                      : <Chip size="small" color="warning" variant="outlined" label="zgjidh" sx={{ height: 20, fontSize: 11 }} />}
                  </Stack>
                  <Select size="small" fullWidth displayEmpty value={value}
                    onChange={(e) => onFieldChange(t.key, e.target.value)}
                    renderValue={(v) => (v ? labelOf(v) : <Box component="span" sx={{ color: 'text.secondary' }}>Lëre bosh</Box>)}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
                    sx={{ fontSize: 13.5 }}>
                    <MenuItem value=""><em>Lëre bosh</em></MenuItem>
                    {FIELD_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                  </Select>
                </Box>
              );
            })}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2, py: 1.25 }}>
            Logoja merret nga profili i kompanisë: mjafton <b>{'{!company_logo!}'}</b> në Word. Për të ndryshuar tekstin ose dizajnin, ndryshojeni dokumentin në Word dhe klikoni «Zëvendëso dokumentin».
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
